import React, { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { IgDivider, IgTooltip, IgTypography } from "@intelligems/ig-theme";
import { ExperienceDto } from "@intelligems/ig-types";
import { Grid } from "@mui/material";

import { getExperienceDtoFromFe } from "../../api/formatters/from-dto/experiences/experiences";
import { useGetOrg } from "../../api/organizations/get-organization";
import { useGetScriptStatus } from "../../api/shops/get-script-status";
import { buildEditUrlPath } from "../../features/experienceTests/utils/utils";
import { DASHBOARD_ROUTES } from "../../routes/routes";
import { updateDashboardFilter } from "../../store/slices/dashboardFilters";
import { setDialog } from "../../store/slices/global";
import { selectedVariations } from "../../store/slices/variations";
import theme from "../../theme";
import { ExperienceFe } from "../../types/schemas/experiences";
import { mpClickHeaderNavigationButton } from "../../utils/mixpanel";
import { PreviewExperience } from "../Dialogs/PreviewExperience";
import { PreviewVariationCode } from "../Dialogs/PreviewVariationCode";
import { RedirectToPluginDialog } from "../Dialogs/RedirectToPluginDialog";

interface HeaderNavigationBtnsProps {
  currentExperience: ExperienceFe;
  selected: "details" | "analytics" | "preview";
}
export const HeaderNavigationBtns = ({
  currentExperience,
  selected,
}: HeaderNavigationBtnsProps) => {
  const navigate = useNavigate();
  const variations = useSelector(selectedVariations);
  const dispatch = useDispatch();
  const organization = useGetOrg();
  const scriptStatus = useGetScriptStatus(organization?.data?.org.id);
  const hasAnalyticsAccess = useMemo(() => {
    return currentExperience.startedAtTs && currentExperience.startedAtTs > 0;
  }, [currentExperience?.startedAtTs]);

  const hasPreviewAccess = useMemo(() => {
    return !currentExperience.endedAtTs || currentExperience?.endedAtTs < 1;
  }, [currentExperience?.endedAtTs]);

  return (
    <Grid
      item
      container
      direction={"row"}
      columnSpacing={1}
      xs={4}
      justifyContent={"flex-end"}
    >
      <Grid item>
        <IgTooltip title={selected !== "details" ? "Go to test setup" : ""}>
          <IgTypography
            onClick={() => {
              if (selected !== "details") {
                mpClickHeaderNavigationButton("details", currentExperience.id!);
                navigate(buildEditUrlPath(currentExperience));
              }
            }}
            sx={{
              fontSize: "14px",
              color:
                selected === "details" ? theme.palette.primary.main : "inherit",
              "&:hover": {
                cursor: selected !== "details" ? "pointer" : "default",
                color: theme.palette.primary.main,
              },
            }}
          >
            Details
          </IgTypography>
        </IgTooltip>
      </Grid>
      <Grid item>
        <IgDivider orientation={"vertical"} />
      </Grid>
      <Grid item>
        <IgTooltip
          title={
            !hasAnalyticsAccess
              ? "Start this test for analytics access"
              : selected !== "analytics"
                ? "Go to analytics"
                : ""
          }
        >
          <IgTypography
            onClick={() => {
              if (selected !== "analytics" && hasAnalyticsAccess) {
                mpClickHeaderNavigationButton(
                  "analytics",
                  currentExperience.id!
                );
                dispatch(
                  updateDashboardFilter({
                    key: "experienceId",
                    value: undefined,
                  })
                );
                navigate(
                  `${
                    DASHBOARD_ROUTES.Dashboard_Overview
                  }?experienceId=${currentExperience.id!}`
                );
              }
            }}
            sx={{
              fontSize: "14px",
              color: !hasAnalyticsAccess
                ? theme.palette.disabled.main
                : selected === "analytics"
                  ? theme.palette.primary.main
                  : "inherit",
              "&:hover": {
                cursor: !hasAnalyticsAccess
                  ? "default"
                  : selected !== "analytics"
                    ? "pointer"
                    : "default",
                color: !hasAnalyticsAccess
                  ? theme.palette.disabled.main
                  : theme.palette.primary.main,
              },
            }}
          >
            Analytics
          </IgTypography>
        </IgTooltip>
      </Grid>

      <>
        <Grid item>
          <IgDivider orientation={"vertical"} />
        </Grid>
        <Grid item>
          <IgTooltip title={"Preview test"}>
            <IgTypography
              onClick={() => {
                if (selected !== "preview") {
                  mpClickHeaderNavigationButton(
                    "preview",
                    currentExperience.id!
                  );
                  if (!scriptStatus.data?.exists) {
                    dispatch(
                      setDialog({
                        dialog: "redirectToPluginDialog",
                        open: true,
                        props: {
                          experience: currentExperience,
                          id: currentExperience.id,
                          path:
                            currentExperience.previewPath ||
                            currentExperience.devicePreview?.path,
                        },
                      })
                    );
                  } else {
                    dispatch(
                      setDialog({
                        dialog: "previewExperiment",
                        open: true,
                        props: {
                          id: currentExperience.id,
                          path:
                            currentExperience.previewPath ||
                            currentExperience.devicePreview?.path,
                        },
                      })
                    );
                  }
                }
              }}
              sx={{
                fontSize: "14px",
                color:
                  selected === "preview"
                    ? theme.palette.primary.main
                    : "inherit",
                "&:hover": {
                  cursor: selected !== "preview" ? "pointer" : "default",
                  color: theme.palette.primary.main,
                },
              }}
            >
              Preview
            </IgTypography>
          </IgTooltip>
        </Grid>
      </>

      <PreviewExperience
        experience={getExperienceDtoFromFe(currentExperience) as ExperienceDto}
        organization={organization.data.org}
      />
      <PreviewVariationCode
        experience={currentExperience}
        organization={organization.data.org}
        variations={variations}
      />
      <RedirectToPluginDialog
        type={"experiment"}
        isLoading={false}
        handleClose={() => {
          dispatch(
            setDialog({ dialog: "redirectToPluginDialog", open: false })
          );
        }}
      />
    </Grid>
  );
};
